import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Calendar, Clock, Users, Briefcase, Car, 
  DollarSign, CheckCircle, XCircle, Star, Loader2, AlertCircle,
  Navigation, Plane, ChevronDown, ChevronUp, Phone, Mail,
  MessageSquare, ExternalLink, CreditCard
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import { formatPickupDate, formatPickupTime } from '../utils/datetime';
import { useTranslation } from '../translations';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { OfferCard } from '../components/customer/OfferCard';
import { TrustBadges } from '../components/trust/TrustBadges';
import { PriceBreakdown } from '../components/trust/PriceBreakdown';
import { CancellationBanner } from '../components/trust/CancellationBanner';
import { EscrowNote } from '../components/trust/EscrowNote';
import { ExitIntentPopup } from '../components/trust/ExitIntentPopup';
import { useABVariant } from '../components/trust/useABVariant';

// Custom markers
const startIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36"><path fill="#FF6B00" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z"/><circle fill="white" cx="12" cy="12" r="6"/><text x="12" y="16" text-anchor="middle" fill="#FF6B00" font-size="10" font-weight="bold">A</text></svg>`),
  iconSize: [24, 36],
  iconAnchor: [12, 36],
});

const endIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36"><path fill="#000000" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z"/><circle fill="white" cx="12" cy="12" r="6"/><text x="12" y="16" text-anchor="middle" fill="#000000" font-size="10" font-weight="bold">B</text></svg>`),
  iconSize: [24, 36],
  iconAnchor: [12, 36],
});

function FitBounds({ from, to }) {
  const map = useMap();
  useEffect(() => {
    if (from && to) {
      const bounds = L.latLngBounds([[from.lat, from.lng], [to.lat, to.lng]]);
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [map, from, to]);
  return null;
}

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, language, loadFromStorage } = useAuthStore();
  const { t } = useTranslation(language);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [expandedTransfer, setExpandedTransfer] = useState(null);
  const [acceptingOffer, setAcceptingOffer] = useState(null);
  const [routeCoordinates, setRouteCoordinates] = useState({});
  const [paymentStatus, setPaymentStatus] = useState(null);
  const { isB: trustB, track: trackTrust } = useABVariant('trust_ui_v1');

  useEffect(() => {
    loadFromStorage();
    setInitialized(true);
    document.title = 'Meine Transfers verwalten | 366Transfer Übersicht';
  }, []);

  useEffect(() => {
    if (initialized) {
      if (token) {
        loadTransfers();
        const paymentResult = searchParams.get('payment');
        const sessionId = searchParams.get('session_id');
        const isPaypal = searchParams.get('paypal');
        const transferId = searchParams.get('transfer_id');
        const offerId = searchParams.get('offer_id');
        
        if (paymentResult === 'success' && isPaypal && transferId && offerId) {
          // PayPal return - capture payment
          capturePaypalPayment(transferId, offerId);
        } else if (sessionId && paymentResult === 'success') {
          // Stripe return
          pollPaymentStatus(sessionId);
        } else if (paymentResult === 'cancelled') {
          setPaymentStatus({ type: 'cancelled', message: 'Zahlung abgebrochen. Sie können es erneut versuchen.' });
        }
      } else {
        setLoading(false);
      }
    }
  }, [token, initialized]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 6;
    if (attempts >= maxAttempts) {
      setPaymentStatus({ type: 'error', message: 'Zahlungsstatus konnte nicht überprüft werden.' });
      return;
    }

    try {
      setPaymentStatus({ type: 'pending', message: 'Zahlung wird verarbeitet...' });
      const res = await api.get(`/payments/status/${sessionId}`);
      
      if (res.data.payment_status === 'paid') {
        setPaymentStatus({ type: 'success', message: 'Zahlung erfolgreich! Ihre Buchung ist bestätigt.' });
        loadTransfers();
        // Clear URL params
        window.history.replaceState({}, '', '/my-transfers');
        return;
      } else if (res.data.status === 'expired') {
        setPaymentStatus({ type: 'error', message: 'Zahlungssitzung abgelaufen.' });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      console.error('Payment status check error:', err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      } else {
        setPaymentStatus({ type: 'error', message: 'Fehler bei der Zahlungsprüfung.' });
      }
    }
  };

  const loadTransfers = async () => {
    try {
      const res = await api.get('/transfers');
      setTransfers(res.data || []);
    } catch (err) {
      console.error('Error loading transfers:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRoute = async (transfer) => {
    if (routeCoordinates[transfer.id]) return;
    
    try {
      const response = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${transfer.pickup_location.lng},${transfer.pickup_location.lat};${transfer.dropoff_location.lng},${transfer.dropoff_location.lat}?overview=full&geometries=geojson`
      );
      const data = await response.json();
      
      if (data.routes && data.routes[0]) {
        const coords = data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);
        setRouteCoordinates(prev => ({ ...prev, [transfer.id]: coords }));
      }
    } catch (error) {
      console.error('Error fetching route:', error);
    }
  };

  const handleExpandTransfer = (transfer) => {
    if (expandedTransfer === transfer.id) {
      setExpandedTransfer(null);
    } else {
      setExpandedTransfer(transfer.id);
      loadRoute(transfer);
    }
  };

  const handleAcceptOffer = async (transferId, offerId, method = 'stripe') => {
    setAcceptingOffer(offerId);
    try {
      if (method === 'paypal') {
        const res = await api.post(`/transfers/${transferId}/pay-paypal/${offerId}`, {
          origin_url: window.location.origin,
        });
        if (res.data.url) {
          window.location.href = res.data.url;
        }
      } else {
        const res = await api.post(`/transfers/${transferId}/pay/${offerId}`, {
          origin_url: window.location.origin,
        });
        if (res.data.url) {
          window.location.href = res.data.url;
        }
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler beim Starten der Zahlung');
      setAcceptingOffer(null);
    }
  };

  const capturePaypalPayment = async (transferId, offerId) => {
    setPaymentStatus({ type: 'pending', message: 'PayPal-Zahlung wird verarbeitet...' });
    try {
      // Find the PayPal order for this transfer
      const token = searchParams.get('token'); // PayPal adds token param
      if (!token) {
        setPaymentStatus({ type: 'error', message: 'PayPal-Zahlungstoken nicht gefunden.' });
        return;
      }
      const res = await api.post(`/paypal/capture/${token}`);
      if (res.data.status === 'completed') {
        setPaymentStatus({ type: 'success', message: 'PayPal-Zahlung erfolgreich! Ihre Buchung ist bestätigt.' });
        loadTransfers();
        window.history.replaceState({}, '', '/my-transfers');
      } else {
        setPaymentStatus({ type: 'error', message: 'PayPal-Zahlung fehlgeschlagen.' });
      }
    } catch (err) {
      console.error('PayPal capture error:', err);
      setPaymentStatus({ type: 'error', message: 'Fehler bei der PayPal-Zahlungsbestätigung.' });
    }
  };

  const formatLocation = (location) => {
    if (!location) return '-';
    const parts = location.address?.split(',') || [];
    return parts.slice(0, 2).join(', ') || `${location.lat?.toFixed(6)}, ${location.lng?.toFixed(6)}`;
  };

  const openInGoogleMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
  };

  // Loading state
  if (!initialized || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="mx-auto text-orange-500 animate-spin mb-4" />
          <p className="text-slate-500">Laden...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!user || !token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-2xl shadow-sm max-w-md">
          <Users size={48} className="mx-auto text-orange-500 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Anmeldung erforderlich</h2>
          <p className="text-slate-500 mb-4">Bitte melden Sie sich an, um Ihre Buchungen zu sehen.</p>
          <a href="/" className="inline-block bg-orange-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-orange-600 transition-colors">
            Zur Anmeldung
          </a>
        </div>
      </div>
    );
  }

  const statusColors = {
    open: 'bg-blue-100 text-blue-700',
    bidding: 'bg-yellow-100 text-yellow-700',
    booked: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-slate-100 text-slate-600',
    cancelled: 'bg-red-100 text-red-700',
  };

  const statusLabels = {
    open: 'Warten auf Angebote',
    bidding: 'Angebote verfügbar',
    booked: 'Gebucht',
    completed: 'Abgeschlossen',
    cancelled: 'Storniert',
  };

  // Add completed count to stats
  const completedCount = transfers.filter(t => t.status === 'completed').length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-dashboard">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm">
              <ArrowLeft size={18} /> Startseite
            </a>
            <div className="flex items-baseline gap-0.5">
              <span className="text-2xl font-extrabold text-orange-500">366</span>
              <span className="text-sm font-semibold text-slate-800">Transfer</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/profile" className="text-sm text-slate-500 hover:text-orange-500 transition-colors" data-testid="profile-link">Mein Profil</a>
            <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <span className="text-sm font-medium text-slate-700">{user?.name}</span>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Meine Transfers</h1>
        <p className="text-slate-500 mb-6">Verwalten Sie Ihre Buchungen und vergleichen Sie Angebote</p>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-100 p-4 text-center">
            <p className="text-2xl font-bold text-slate-800">{transfers.length}</p>
            <p className="text-xs text-slate-500">Anfragen gesamt</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{transfers.filter(t => t.status === 'bidding').length}</p>
            <p className="text-xs text-slate-500">Mit Angeboten</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{transfers.filter(t => t.status === 'booked').length}</p>
            <p className="text-xs text-slate-500">Gebucht</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4 text-center">
            <p className="text-2xl font-bold text-slate-600">{completedCount}</p>
            <p className="text-xs text-slate-500">Abgeschlossen</p>
          </div>
        </div>

        {/* Payment Status Banner */}
        {paymentStatus && (
          <div className={`mb-6 rounded-xl p-4 flex items-center gap-3 ${
            paymentStatus.type === 'success' ? 'bg-emerald-50 border border-emerald-200' :
            paymentStatus.type === 'pending' ? 'bg-blue-50 border border-blue-200' :
            paymentStatus.type === 'cancelled' ? 'bg-amber-50 border border-amber-200' :
            'bg-red-50 border border-red-200'
          }`} data-testid="payment-status-banner">
            {paymentStatus.type === 'success' && <CheckCircle size={20} className="text-emerald-600 flex-shrink-0" />}
            {paymentStatus.type === 'pending' && <Loader2 size={20} className="text-blue-600 animate-spin flex-shrink-0" />}
            {paymentStatus.type === 'cancelled' && <AlertCircle size={20} className="text-amber-600 flex-shrink-0" />}
            {paymentStatus.type === 'error' && <XCircle size={20} className="text-red-600 flex-shrink-0" />}
            <p className={`text-sm font-medium ${
              paymentStatus.type === 'success' ? 'text-emerald-700' :
              paymentStatus.type === 'pending' ? 'text-blue-700' :
              paymentStatus.type === 'cancelled' ? 'text-amber-700' :
              'text-red-700'
            }`}>{paymentStatus.message}</p>
            {paymentStatus.type !== 'pending' && (
              <button onClick={() => setPaymentStatus(null)} className="ml-auto text-slate-400 hover:text-slate-600">
                <XCircle size={16} />
              </button>
            )}
          </div>
        )}

        {/* Transfers List */}
        {transfers.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
            <Navigation size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Keine Transfers</h3>
            <p className="text-slate-500 mb-4">Sie haben noch keine Transfer-Anfragen gestellt.</p>
            <a href="/" className="inline-block bg-orange-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-orange-600 transition-colors">
              Transfer buchen
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            {transfers.map(transfer => (
              <div key={transfer.id} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                {/* Transfer Header */}
                <div 
                  className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => handleExpandTransfer(transfer)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Route */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="flex flex-col items-center">
                          <div className="w-3 h-3 rounded-full bg-orange-500" />
                          <div className="w-0.5 h-6 bg-slate-200" />
                          <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{formatLocation(transfer.pickup_location)}</p>
                          <p className="text-sm font-medium text-slate-800 mt-4">{formatLocation(transfer.dropoff_location)}</p>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {formatPickupDate(transfer.pickup_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {formatPickupTime(transfer.pickup_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={12} />
                          {transfer.adults + (transfer.children || 0)} Pers.
                        </span>
                        <span className="flex items-center gap-1">
                          <Navigation size={12} />
                          {transfer.distance_km} km
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${statusColors[transfer.status]}`}>
                          {statusLabels[transfer.status]}
                        </span>
                        {transfer.offers_count > 0 && (
                          <p className="text-sm font-semibold text-orange-500 mt-2">
                            {transfer.offers_count} Angebot{transfer.offers_count > 1 ? 'e' : ''}
                          </p>
                        )}
                      </div>
                      {expandedTransfer === transfer.id ? (
                        <ChevronUp size={20} className="text-slate-400" />
                      ) : (
                        <ChevronDown size={20} className="text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedTransfer === transfer.id && (
                  <div className="border-t border-slate-100">
                    {/* Mini Map */}
                    <div className="relative h-[200px]">
                      <MapContainer
                        center={[
                          (transfer.pickup_location.lat + transfer.dropoff_location.lat) / 2,
                          (transfer.pickup_location.lng + transfer.dropoff_location.lng) / 2
                        ]}
                        zoom={10}
                        className="w-full h-full"
                        scrollWheelZoom={false}
                      >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <Marker position={[transfer.pickup_location.lat, transfer.pickup_location.lng]} icon={startIcon} />
                        <Marker position={[transfer.dropoff_location.lat, transfer.dropoff_location.lng]} icon={endIcon} />
                        {routeCoordinates[transfer.id] && (
                          <Polyline positions={routeCoordinates[transfer.id]} pathOptions={{ color: '#22C55E', weight: 4 }} />
                        )}
                        <FitBounds from={transfer.pickup_location} to={transfer.dropoff_location} />
                      </MapContainer>
                      
                      {/* Route Info Overlay */}
                      <div className="absolute bottom-3 left-3 bg-white/95 rounded-lg px-3 py-2 shadow-lg z-[1000]">
                        <div className="flex items-center gap-3 text-sm font-bold text-slate-800">
                          <span>{transfer.distance_km} km</span>
                          <span className="text-slate-300">|</span>
                          <span>~{transfer.duration_minutes} min</span>
                        </div>
                      </div>
                    </div>

                    {/* Offers Section */}
                    <div className="p-5">
                      {trustB && transfer.status !== 'booked' && transfer.offers?.length > 0 && (
                        <TrustBadges variant="banner" t={t} />
                      )}
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">
                        {transfer.offers?.length > 0 ? `Angebote (${transfer.offers.length})` : 'Noch keine Angebote'}
                      </h4>

                      {transfer.status === 'booked' && transfer.booked_offer && (
                        <div className="bg-emerald-50 rounded-xl p-4 mb-4">
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle size={18} className="text-emerald-600" />
                            <span className="font-semibold text-emerald-700">Gebuchter Fahrer</span>
                          </div>
                          <div className="flex items-start justify-between">
                            <div>
                              <a
                                href={`/driver/public/${transfer.booked_offer.driver_id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="font-medium text-slate-800 hover:text-orange-500 transition-colors flex items-center gap-1.5"
                                data-testid={`booked-driver-link-${transfer.id}`}
                              >
                                {transfer.booked_offer.driver_name}
                                <ExternalLink size={12} className="text-slate-400" />
                              </a>
                              <p className="text-sm text-slate-500">{transfer.booked_offer.vehicle?.make} {transfer.booked_offer.vehicle?.model}</p>
                              {transfer.booked_offer.driver_phone && (
                                <a href={`tel:${transfer.booked_offer.driver_phone}`} className="flex items-center gap-1 text-sm text-orange-500 mt-2">
                                  <Phone size={14} /> {transfer.booked_offer.driver_phone}
                                </a>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-emerald-600">€{transfer.booked_offer.price}</p>
                              <p className="text-xs text-emerald-500 flex items-center gap-1 justify-end"><CheckCircle size={12} /> Bezahlt</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {transfer.status !== 'booked' && transfer.offers?.length > 0 && (
                        <div className="space-y-3">
                          {transfer.offers.map(offer => (
                            <div key={offer.id} data-testid={`offer-wrap-${offer.id}`}>
                              <OfferCard
                                offer={offer}
                                transfer={transfer}
                                t={t}
                                acceptingOffer={acceptingOffer}
                                onAccept={handleAcceptOffer}
                              />
                              {trustB && offer.status === 'pending' && (
                                <div className="mt-2 space-y-2 px-1">
                                  <EscrowNote t={t} testIdSuffix={offer.id} />
                                  <PriceBreakdown totalPrice={offer.price} t={t} testIdSuffix={offer.id} />
                                  <CancellationBanner t={t} testIdSuffix={offer.id} />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {transfer.offers?.length === 0 && transfer.status === 'open' && (
                        <div className="text-center py-6 text-slate-400">
                          <Loader2 size={24} className="mx-auto mb-2 animate-spin" />
                          <p className="text-sm">Warten auf Angebote von Fahrern...</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {trustB && <ExitIntentPopup t={t} whatsappNumber={process.env.REACT_APP_SUPPORT_WHATSAPP} />}
    </div>
  );
}
